<template>
  <main class="main">
    <BreadCrumbs :pages="pages" />

    <section class="main__about about">
      <div class="about__container">
        <div class="about__box">
          <div class="about-row">
            <div class="about-row__part about-row__head">
              <div class="about-row__preview">
                <nuxt-img
                  :src="$getImage(project.project_image)"
                  :alt="project.title + ' ' + $t('common.at') + ' ' + $config.appName"
                  :title="project.title"
                  placeholder
                  loading="lazy"
                  @error="$event.target.src = $config.mediaUrl + 'img/projects/default.webp'"
                />
              </div>
              <div class="about-row__article-head">
                <h1 class="about-row__title">
                  {{ project.title }}
                </h1>
              </div>
            </div>
            <div class="about-row__volume about-volume">
              <div class="about-volume__part">
                <div class="about-volume__part-wrapper">
                  <span class="about-volume__label">
                    {{ $t('filter.volume') }}(24{{ $t('common.h') }}):
                    <span v-tippy="{...tippyOptions, 'content': getContentById('volume_tooltip')}" class="tip">
                      <i class="bx bxs-info-circle"></i>
                    </span>
                  </span>
                  <div class="about-volume__value">
                    <span>
                      ${{ $priceFormat(project.volume_24h, 2) }}
                    </span>
                    <div :class="{'about-volume-rate': true, 'plus': project.volume_24h_procents > 0, 'minus': project.volume_24h_procents < 0}">
                      <div class="about-volume-rate__icon">
                        <i v-if="project.volume_24h_procents > 0" class="bx bx-trending-down"></i>
                        <i v-else-if="project.volume_24h_procents < 0" class="bx bx-trending-up"></i>
                      </div>
                      <span class="about-volume-rate__percent">
                        {{ project.volume_24h_procents !== null ? $priceFormat(project.volume_24h_procents, 2) + '%' : '--' }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="about-volume__part">
                <div class="about-volume__part-wrapper">
                  <span class="about-volume__label">
                    {{ $t('filter.volume') }}(7{{ $t('common.d') }}):
                  </span>
                  <div class="about-volume__value">
                    <span>
                      ${{ $priceFormat(project.volume_7d, 2) }}
                    </span>
                    <div :class="{'about-volume-rate': true, 'plus': project.volume_7d_procents > 0, 'minus': project.volume_7d_procents < 0}">
                      <div class="about-volume-rate__icon">
                        <i v-if="project.volume_7d_procents > 0" class="bx bx-trending-down"></i>
                        <i v-else-if="project.volume_7d_procents < 0" class="bx bx-trending-up"></i>
                      </div>
                      <span class="about-volume-rate__percent">
                        {{ project.volume_7d_procents !== null ? $priceFormat(project.volume_7d_procents, 2) + '%' : '--' }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="about-row__part about-row__actions actions-about">
              <div class="actions-about__wrapper">
                <a v-if="project.site_url !== null" :href="$checkProtocol(project.site_url)" rel="nofollow noopener noreferrer" target="_blank" class="actions-about__button actions-about__button_filled">
                  <div class="actions-about__button-icon">
                    <i class="bx bx-globe"></i>
                  </div>
                  <span class="actions-about__button-text">
                    {{ $t('project.website') }}
                  </span>
                </a>
                <nuxt-link v-if="user === null" :to="localePath('/login')" class="actions-about__button actions-about__button_stroke">
                  <div class="actions-about__button-icon">
                    <i class="bx bxs-star"></i>
                  </div>
                  <span class="actions-about__button-text">
                    {{ $t('project.add_watchlist') }}
                  </span>
                </nuxt-link>
                <button v-else-if="watchlistLoading" type="button" class="actions-about__button actions-about__button_stroke">
                  <span class="actions-about__button-text">
                    <i class="bx bx-loader-alt loading"></i>
                  </span>
                </button>
                <button v-else-if="!insideWatchlist" type="button" class="actions-about__button actions-about__button_stroke" @click="toggleWatchlist">
                  <div class="actions-about__button-icon">
                    <i class="bx bxs-star"></i>
                  </div>
                  <span class="actions-about__button-text">
                    {{ $t('project.add_watchlist') }}
                  </span>
                </button>
                <button v-else type="button" class="actions-about__button actions-about__button_stroke" @click="toggleWatchlist">
                  <div class="actions-about__button-icon">
                    <i class="bx bx-star"></i>
                  </div>
                  <span class="actions-about__button-text">
                    {{ $t('project.remove_watchlist') }}
                  </span>
                </button>
              </div>
            </div>
          </div>
          <div v-if="project.description !== null && project.description.length > 0" class="about-article">
            <h2 class="about-article__title">
              {{ $t('project.about') }}
              {{ project.title }}
            </h2>
            <p class="about-article__paragraph">
              {{ project.description }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <section class="main__currency-info currency-info">
      <div class="currency-info__container">
        <div class="currency-info__part currency-graph">
          <h3 class="currency-graph__title">
            {{ $t('filter.volume') }}
          </h3>
          <div class="currency-graph__content">
            <div v-if="project.price_change !== null" ref="mainChart" class="currency-graph__graph">
              <client-only>
                <LineChart :chart-data="lineChart.data" :options="lineChart.options" />
              </client-only>
              <div v-if="loadingChart" class="loading-area">
                <div class="icon">
                  <i class="bx bx-loader-alt loading"></i>
                </div>
                <div class="title">
                  {{ $t('common.loading') }}...
                </div>
              </div>
            </div>
            <div v-else class="currency-graph__graph not-found-area">
              <Empty />
              <div class="not-found-header">
                {{ $t('project.chart_not_ready') }}
              </div>
            </div>
          </div>
        </div>

        <div class="currency-info__part currency-characteristic">
          <h3 class="currency-characteristic__title">
            {{ $t('project.financial') }}
          </h3>
          <div class="currency-characteristic__list">
            <div class="currency-characteristic__el">
              <span class="currency-characteristic__name">
                {{ $t('project.platform') }}
              </span>
              <div class="currency-characteristic__value">
                {{ project.platform !== null ? project.platform : '--' }}
              </div>
            </div>
            <div class="currency-characteristic__el">
              <span class="currency-characteristic__name">
                {{ $t('project.address') }}
              </span>
              <div class="currency-characteristic__value">
                {{ project.address !== null ? $shortenAddress(project.address) : '--' }}
                <span class="copy-me" :text-tooltip="copyText" flow="left" @click="copy($evt, project.address)">
                  <i class="bx bx-copy"></i>
                </span>
              </div>
            </div>
            <div class="currency-characteristic__el">
              <span class="currency-characteristic__name">
                {{ $t('project.daily_percent_change') }}
              </span>
              <div :class="{'currency-characteristic__value': true, 'plus': project.volume_24h_procents > 0, 'minus': project.volume_24h_procents < 0}">
                <div class="currency-characteristic__icon">
                  <i v-if="project.volume_24h_procents > 0" class="bx bx-trending-down"></i>
                  <i v-else-if="project.volume_24h_procents < 0" class="bx bx-trending-up"></i>
                </div>
                <div class="currency-characteristic__percent">
                  {{ project.volume_24h_procents !== null ? $priceFormat(project.volume_24h_procents, 2) + '%' : '--' }}
                </div>
              </div>
            </div>
            <div class="currency-characteristic__el">
              <span class="currency-characteristic__name">
                {{ $t('common.marketcap') }}
              </span>
              <div class="currency-characteristic__value">
                {{ project.soft_cap !== null ? '$' + $priceFormat(project.soft_cap, 0) : '--' }}
              </div>
            </div>
            <div class="currency-characteristic__el">
              <span class="currency-characteristic__name">
                {{ $t('common.tvl') }}
              </span>
              <div class="currency-characteristic__value">
                {{ project.hard_cap !== null ? '$' + $priceFormat(project.hard_cap, 0) : '--' }}
              </div>
            </div>
            <div class="currency-characteristic__el">
              <span class="currency-characteristic__name">
                {{ $t('common.fdv') }}
              </span>
              <div class="currency-characteristic__value">
                {{ project.max_supply !== null ? '$' + $priceFormat(project.max_supply, 0) : '--' }}
              </div>
            </div>
            <div class="currency-characteristic__el">
              <span class="currency-characteristic__name">
                {{ $t('project.weekly_percent_change') }}
              </span>
              <div :class="{'currency-characteristic__value': true, 'plus': project.volume_7d_procents > 0, 'minus': project.volume_7d_procents < 0}">
                <div class="currency-characteristic__icon">
                  <i v-if="project.volume_7d_procents > 0" class="bx bx-trending-down"></i>
                  <i v-else-if="project.volume_7d_procents < 0" class="bx bx-trending-up"></i>
                </div>
                <div class="currency-characteristic__percent">
                  {{ project.volume_7d_procents !== null ? $priceFormat(project.volume_7d_procents, 2) + '%' : '--' }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="currency-info__part live-chat">
          <h3 class="live-chat__title">
            {{ $t('project.live_chat') }}
          </h3>
          <div class="live-chat__chat live-chat-box">
            <div class="live-chat-box__post-comment post-comment">
              <div class="post-comment__header">
                <div class="live-chat__user user-live-chat">
                  <div class="user-live-chat__avatar">
                    <nuxt-img
                      v-if="user !== null"
                      :src="$getImage(user.avatar, 'users')"
                      :alt="user.name + ' ' + $t('common.at') + ' ' + $config.appName"
                      :title="user.name"
                      placeholder
                      loading="lazy"
                      @error="$event.target.src = $config.mediaUrl + 'img/users/default.webp'"
                    />
                    <nuxt-img
                      v-else
                      :src="$getImage(null, 'users')"
                      :alt="$t('common.login_to') + ' ' + $config.appName"
                      :title="$t('common.login_first')"
                      placeholder
                      loading="lazy"
                      @error="$event.target.src = $config.mediaUrl + 'img/users/default.webp'"
                    />
                  </div>
                  <div v-if="user !== null" class="user-live-chat__about">
                    <span class="user-live-chat__name">
                      {{ user.name }}
                    </span>
                    <span class="user-live-chat__username">
                      @{{ user.username }}
                    </span>
                  </div>
                  <div v-else class="user-live-chat__about">
                    <span class="user-live-chat__name">
                      {{ $t('common.login_first') }}
                    </span>
                    <span class="user-live-chat__username">@username</span>
                  </div>
                </div>
                <div class="post-comment__actions">
                  <button type="button" :class="{'post-comment__button post-comment__button_green': true, 'active': userMessage.type === 'bullish'}" @click="setCommentType('bullish')">
                    <div class="post-comment__button-icon">
                      <i class="bx bxs-up-arrow"></i>
                    </div>
                    <span>
                      {{ $t('project.bullish') }}
                    </span>
                  </button>
                  <button type="button" :class="{'post-comment__button post-comment__button_red': true, 'active': userMessage.type === 'bearish'}" @click="setCommentType('bearish')">
                    <div class="post-comment__button-icon">
                      <i class="bx bxs-down-arrow"></i>
                    </div>
                    <span>
                      {{ $t('project.bearish') }}
                    </span>
                  </button>
                </div>
              </div>
              <div class="post-comment__area">
                <span v-if="userMessage.reply_to !== null && replyTo !== null" class="user-live-chat__name">
                  {{ $t('project.replying_to') }}
                  {{ replyTo.name }} (@{{ replyTo.username }})
                </span>
                <textarea v-model="userMessage.message" class="post-comment__input" :placeholder="$t('project.your_message')"></textarea>
                <button type="button" :class="{'post-comment__button': true, 'disabled': user === null || loadingMessage}" :aria-label="$t('project.send_message')" @click="sendChatMessage">
                  <i v-if="!loadingMessage" class="bx bx-paper-plane"></i>
                  <i v-else class="bx bx-loader-alt loading"></i>
                </button>
              </div>
              <div v-if="user === null" class="login__overlay">
                <nuxt-link :to="localePath('/login')" class="button filled-button">
                  {{ $t('project.login_to_discuss') }}
                </nuxt-link>
              </div>
            </div>

            <div v-if="!loading && messages.length > 0" ref="chatArea" class="comments-list">
              <div v-for="(msg, ind) in messages" :key="ind" class="live-chat-box__comment post-comment">
                <div class="post-comment__header">
                  <div class="live-chat__user user-live-chat">
                    <div class="user-live-chat__avatar">
                      <nuxt-img
                        :src="$getImage(msg.author_image, 'users')"
                        :alt="msg.name + ' ' + $t('common.at') + ' ' + $config.appName"
                        :title="msg.name"
                        placeholder
                        loading="lazy"
                        @error="$event.target.src = $config.mediaUrl + 'img/users/default.webp'"
                      />
                    </div>
                    <div class="user-live-chat__about">
                      <span class="user-live-chat__name">
                        {{ msg.name }}
                      </span>
                      <span class="user-live-chat__username">
                        @{{ msg.username }}
                      </span>
                    </div>
                  </div>
                  <span class="post-comment__remaining">
                    {{ $realisticDate(msg.created_at) }}
                  </span>
                  <div class="post-comment__actions">
                    <div v-if="msg.type === 'bullish'" class="post-comment__button post-comment__button_green">
                      <div class="post-comment__button-icon">
                        <i class="bx bxs-up-arrow"></i>
                      </div>
                      <span>
                        {{ $t('project.bullish') }}
                      </span>
                    </div>
                    <div v-else-if="msg.type === 'bearish'" class="post-comment__button post-comment__button_red">
                      <div class="post-comment__button-icon">
                        <i class="bx bxs-down-arrow"></i>
                      </div>
                      <span>
                        {{ $t('project.bearish') }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="post-comment__comment">
                  <div v-dompurify-html="msg.message"></div>
                  <span class="post-comment__remaining">
                    {{ $realisticDate(msg.created_at) }}
                  </span>
                </div>
                <div class="user-actions">
                  <div class="user-actions__action">
                    <div v-if="user === null" class="user-actions__button disabled">
                      <div class="user-actions__icon">
                        <i class="bx bxs-heart"></i>
                      </div>
                    </div>
                    <button
                      v-else-if="msg.like_date === null"
                      type="button"
                      class="user-actions__button"
                      :aria-label="$t('project.like')"
                      @click="likeMessage(msg.mid)"
                    >
                      <div class="user-actions__icon">
                        <i v-if="chatActionsLoading.like !== msg.mid" class="bx bx-heart"></i>
                        <i v-else class="bx bx-loader-alt loading"></i>
                      </div>
                    </button>
                    <button
                      v-else-if="msg.like_date !== null"
                      type="button"
                      class="user-actions__button"
                      :aria-label="$t('project.dislike')"
                      @click="unlikeMessage(msg.mid)"
                    >
                      <div class="user-actions__icon">
                        <i v-if="chatActionsLoading.like !== msg.mid" class="bx bxs-heart"></i>
                        <i v-else class="bx bx-loader-alt loading"></i>
                      </div>
                    </button>
                    <span class="user-actions__count">
                      {{ $getBigNumber(msg.likes) }}
                    </span>
                  </div>
                  <div class="user-actions__action">
                    <button type="button" class="user-actions__button disabled" :aria-label="$t('project.report')" :text-tooltip="$t('project.report')">
                      <div class="user-actions__icon">
                        <i class="bx bxs-flag-alt"></i>
                      </div>
                    </button>
                  </div>
                  <div class="user-actions__action">
                    <div v-if="user === null" class="user-actions__button disabled" :text-tooltip="$t('project.share')">
                      <div class="user-actions__icon">
                        <i class="bx bxs-share"></i>
                      </div>
                    </div>
                    <button
                      v-else
                      type="button"
                      class="user-actions__button"
                      :aria-label="$t('project.reply')"
                      :text-tooltip="$t('project.reply')"
                      @click="setReplyTo(msg.mid)"
                    >
                      <div class="user-actions__icon">
                        <i class="bx bxs-share"></i>
                      </div>
                    </button>
                  </div>
                  <div class="user-actions__action">
                    <button
                      v-if="user !== null && msg.uid === user.id"
                      type="button"
                      class="user-actions__button"
                      :aria-label="$t('project.remove')"
                      :text-tooltip="$t('project.remove')"
                      @click="removeMessage(msg.mid)"
                    >
                      <div class="user-actions__icon">
                        <i class="bx bxs-trash"></i>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div v-else-if="loading" ref="chatArea" class="comments-list">
              <div v-for="index in 5" :key="index" class="live-chat-box__comment post-comment">
                <div class="post-comment__header">
                  <div class="live-chat__user user-live-chat">
                    <div class="user-live-chat__avatar">
                      <Skeleton width="28px" height="28px" circle />
                    </div>
                    <div class="user-live-chat__about">
                      <Skeleton width="73px" height="15px" class="user-live-chat__name" />
                      <Skeleton width="50px" height="12px" class="user-live-chat__username" />
                    </div>
                  </div>
                  <Skeleton width="60px" height="12px" class="post-comment__remaining" />
                  <div class="post-comment__actions">
                    <Skeleton width="67px" height="28px" class="post-comment__button" />
                  </div>
                </div>
                <div class="post-comment__comment">
                  <Skeleton width="200px" height="14px" style="margin-bottom: .5rem;" />
                  <Skeleton width="130px" height="14px" />
                  <Skeleton width="60px" height="12px" class="post-comment__remaining" />
                </div>
                <div class="user-actions">
                  <div class="user-actions__action">
                    <div class="user-actions__button disabled">
                      <div class="user-actions__icon">
                        <i class="bx bxs-heart"></i>
                      </div>
                    </div>
                    <Skeleton width="15px" height="12px" class="user-actions__count" />
                  </div>
                  <div class="user-actions__action">
                    <div class="user-actions__button disabled">
                      <div class="user-actions__icon">
                        <i class="bx bxs-flag-alt"></i>
                      </div>
                    </div>
                  </div>
                  <div class="user-actions__action">
                    <div class="user-actions__button disabled">
                      <div class="user-actions__icon">
                        <i class="bx bxs-share"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else ref="chatArea" class="comments-list not-found-area">
              <Empty />
              <div class="not-found-header">
                {{ $t('project.no_comment_yet') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="main__watchlist watchlist watchlist_mod watchlist_currency shorten">
      <div class="watchlist__container">
        <div class="watchlist__header watchlist-header">
          <h2 class="watchlist-header__title mr-4">
            {{ project.title }}
            {{ $t('project.markets') }}
          </h2>
          <ul class="watchlist-categories">
            <li
              v-for="(cat, ind) in categories"
              :key="ind"
              :class="{'watchlist-categories__el': true, 'watchlist-categories__el_curent': cat === pairsFilter.category || (cat === 'all' && pairsFilter.category === null)}"
              @click="selectPairCategory(cat)"
            >
              <span class="watchlist-categories__text">
                {{ $t(`project.${cat}`) }}
              </span>
            </li>
          </ul>
          <div class="watchlist-showing">
            <span class="watchlist-showing__title" data-v-2a183b29="">
              {{ $t('project.pairs_with') }}
            </span>
            <div :class="{'watchlist-showing__select select': true, 'is-active': dropdowns.pair}">
              <div class="select__header" @click="toggleDropdown('pair')">
                <div class="select__current">
                  {{ pairsFilter.pair !== null ? pairsFilter.pair : $t('project.any_coins') }}
                </div>
                <div class="watchlist-showing__icon">
                  <i class="bx bxs-down-arrow"></i>
                </div>
              </div>
              <div class="select__body">
                <span v-for="fp in filterPairs" :key="fp" class="select__item" @click="changeFilterPair(fp)">
                  {{ fp !== 'all' ? fp : $t('project.any_coins') }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div v-if="!pairsInit || pairsTotal > 0" class="watchlist-main">
          <div class="watchlist-main__wrapper">
            <div class="watchlist-main__header watchlist-legends one-field">
              <div class="watchlist-legend watchlist-legends__order">
                <div class="watchlist-legends__name">
                  #
                </div>
              </div>
              <div class="watchlist-legend watchlist-legends__title">
                <div class="watchlist-legends__name">
                  {{ $t('project.name') }}
                </div>
              </div>
              <div class="watchlist-legend">
                <div class="watchlist-legends__name">
                  {{ $t('project.pair') }}
                </div>
              </div>
              <div class="watchlist-legend sortable" @click="changeSort('price')">
                <div class="watchlist-legends__name">
                  {{ $t('project.price') }}
                  <div v-if="pairsFilter.orderField.includes('price')" class="watchlist-legends__icon">
                    <i v-if="pairsFilter.orderField.toLowerCase() === 'price_desc'" class="bx bxs-down-arrow"></i>
                    <i v-if="pairsFilter.orderField.toLowerCase() === 'price_asc'" class="bx bxs-up-arrow"></i>
                  </div>
                </div>
              </div>
              <div class="watchlist-legend">
                <div class="watchlist-legends__name">
                  {{ $t('project.fee_type') }}
                  <span v-tippy="{...tippyOptions, 'content': getContentById('fee_type')}" class="tip">
                    <i class="bx bxs-info-circle"></i>
                  </span>
                </div>
              </div>
              <div class="watchlist-legend">
                <div class="watchlist-legends__name">
                  {{ $t('project.currency_type') }}
                  <span v-tippy="{...tippyOptions, 'content': getContentById('currency_type')}" class="tip">
                    <i class="bx bxs-info-circle"></i>
                  </span>
                </div>
              </div>
              <div class="watchlist-legend sortable" @click="changeSort('volume')">
                <div class="watchlist-legends__name">
                  {{ $t('project.volume') }}
                  <div v-if="pairsFilter.orderField.includes('volume')" class="watchlist-legends__icon">
                    <i v-if="pairsFilter.orderField.toLowerCase() === 'volume_desc'" class="bx bxs-down-arrow"></i>
                    <i v-if="pairsFilter.orderField.toLowerCase() === 'volume_asc'" class="bx bxs-up-arrow"></i>
                  </div>
                </div>
              </div>
              <div class="watchlist-legend">
                <div class="watchlist-legends__name">
                  {{ $t('project.volume') }}, %
                </div>
              </div>
              <div class="watchlist-legend">
                <div class="watchlist-legends__name">
                  {{ $t('project.volume_base') }}
                  <span v-tippy="{...tippyOptions, 'content': getContentById('volume_base')}" class="tip">
                    <i class="bx bxs-info-circle"></i>
                  </span>
                </div>
              </div>
              <div class="watchlist-legend">
                <div class="watchlist-legends__name">
                  {{ $t('project.updated') }}
                </div>
              </div>
            </div>
            <div v-if="(!pairsLoading || pairsFilter.page > 1) && pairsTotal > 0" class="watchlist-main__list">
              <div v-for="(pair, ind) in pairs" :key="ind" class="watchlist-product">
                <div class="watchlist-product__wrapper one-field">
                  <div class="watchlist-product__part watchlist-product__part_actions">
                    <span class="watchlist-product__num">
                      {{ ind + 1 }}
                    </span>
                  </div>
                  <div class="watchlist-product__part watchlist-product-name">
                    <div class="watchlist-product-name__box">
                      <nuxt-link v-if="pair.slug !== undefined && pair.title !== null" :to="$getProjectLink(pair.type || pair.category, pair.slug)" class="watchlist-product-name__icon">
                        <nuxt-img
                          :src="$getImage(pair.image, 'projects')"
                          :alt="pair.title + ' ' + $t('common.at') + ' ' + $config.appName"
                          :title="pair.title"
                          :quality="80"
                          format="webp"
                          loading="lazy"
                          @error="$event.target.src = $config.mediaUrl + 'img/projects/default.webp'"
                        />
                      </nuxt-link>
                      <div v-else class="watchlist-product-name__icon">
                        <nuxt-img
                          :src="$getImage(pair.image, 'projects')"
                          :alt="pair.title + ' ' + $t('common.at') + ' ' + $config.appName"
                          :title="pair.title"
                          :quality="80"
                          format="webp"
                          loading="lazy"
                          @error="$event.target.src = $config.mediaUrl + 'img/projects/default.webp'"
                        />
                      </div>
                      <nuxt-link v-if="pair.slug !== undefined && pair.title !== null" :to="$getProjectLink(pair.type || pair.category, pair.slug)" class="watchlist-product-name__name" :title="pair.title">
                        <span class="watchlist-product-name__full-name">
                          <i v-if="pair.premium !== null" class="bx bxs-zap premium_icon"></i>
                          {{ pair.title }}
                        </span>
                      </nuxt-link>
                      <div v-else class="watchlist-product-name__name" :title="pair.curr_from">
                        <span class="watchlist-product-name__full-name">
                          {{ pair.curr_from }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="watchlist-product__part watchlist-product-single pairs">
                    {{ pair.curr_from + '/' + pair.curr_to }}
                  </div>
                  <div class="watchlist-product__part watchlist-product-single watchlist-product__price">
                    <span v-if="pair.price !== null && pair.price >= 0.0001" class="watchlist-product__text">
                      ${{ pair.price > 1 ? $priceFormat(pair.price, 2) : $formatCryptoAmount(pair.price, 6) }}
                    </span>
                    <span v-else-if="pair.price !== null" class="watchlist-product__text" :text-tooltip="'$' + $formatCryptoAmount(pair.price)">
                      ${{ generateCutPrice(pair.price) }}
                    </span>
                    <span v-else class="watchlist-product__text">
                      $...
                    </span>
                  </div>
                  <div class="watchlist-product__part watchlist-product-single watchlist-product-nums fee-type">
                    {{ $t(`project.${pair.fee}`) }}
                  </div>
                  <div class="watchlist-product__part watchlist-product-single watchlist-product-nums currency-type">
                    {{ getPairShortcut(pair.pair) }}
                  </div>
                  <div class="watchlist-product__part watchlist-product-single watchlist-product-nums volume">
                    {{ pair.volume_24h !== null && pair.volume_24h > 0 ? '$' + $priceFormat(pair.volume_24h, 0) : '--' }}
                  </div>
                  <div class="watchlist-product__part watchlist-product-single watchlist-product-nums volume-percent">
                    {{ pair.volume_percent !== null ? (pair.volume_percent > 0.01 ? $priceFormat(pair.volume_percent, 2) + '%' : '&lt;0.01%') : '--' }}
                  </div>
                  <div class="watchlist-product__part watchlist-product-single volume-base">
                    {{ $priceFormat(pair.volume_base, 2) }}
                    {{ pair.curr_from }}
                  </div>
                  <div class="watchlist-product__part watchlist-product-single upd">
                    {{ $realisticDate(pair.updated_at) }}
                  </div>
                </div>
              </div>
            </div>
            <div v-else-if="(pairsLoading && pairsFilter.page === 1) || !pairsInit" class="watchlist-main__list">
              <div v-for="ind in 10" :key="ind" class="watchlist-product">
                <div class="watchlist-product__wrapper one-field">
                  <div class="watchlist-product__part watchlist-product__part_actions">
                    <span class="watchlist-product__num">
                      {{ ind }}
                    </span>
                  </div>
                  <div class="watchlist-product__part watchlist-product-name">
                    <div class="watchlist-product-name__box">
                      <Skeleton width="25px" height="25px" class="watchlist-product-name__icon" />
                      <Skeleton width="65px" height="16px" class="watchlist-product-name__name" />
                    </div>
                  </div>
                  <div class="watchlist-product__part watchlist-product-single pairs">
                    <Skeleton width="70px" height="16px" />
                  </div>
                  <div class="watchlist-product__part watchlist-product-single watchlist-product__price">
                    <Skeleton width="75px" height="16px" />
                  </div>
                  <div class="watchlist-product__part watchlist-product-single watchlist-product-nums depth-minus">
                    <Skeleton width="80px" height="16px" />
                  </div>
                  <div class="watchlist-product__part watchlist-product-single watchlist-product-nums depth-plus">
                    <Skeleton width="80px" height="16px" />
                  </div>
                  <div class="watchlist-product__part watchlist-product-single watchlist-product-nums volume">
                    <Skeleton width="80px" height="16px" />
                  </div>
                  <div class="watchlist-product__part watchlist-product-single watchlist-product-nums volume-percent">
                    <Skeleton width="40px" height="16px" />
                  </div>
                  <div class="watchlist-product__part watchlist-product-single volume-base">
                    <Skeleton width="70px" height="16px" />
                  </div>
                  <div class="watchlist-product__part watchlist-product-single upd">
                    <Skeleton width="90px" height="16px" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="watchlist-main">
          <div class="watchlist-main__wrapper">
            <div class="not-found-area">
              <Empty />
              <div class="not-found-header">
                {{ $t('project.no_pairs_yet') }}
              </div>
            </div>
          </div>
        </div>

        <div v-if="pairsTotal >= pairsFilter.page * pairsFilter.step || pairsLoading" class="section-button">
          <button type="button" :class="{'section-button__button': true, 'disabled': pairsLoading}" :disabled="pairsLoading" @click="loadMorePairs">
            <span v-if="!pairsLoading">
              {{ $t('project.load_more_pairs') }}
            </span>
            <span v-else>
              <i class="bx bx-loader-alt loading"></i>
            </span>
          </button>
        </div>
      </div>
    </section>

    <section v-if="!articlesLoading && articles.length > 0" class="main__news news">
      <div class="news__container">
        <h2 class="news__title">
          {{ project.title }}
          {{ $t('project.news') }}
        </h2>

        <div v-if="!articlesLoading" class="news__list">
          <ArticleCard v-for="(article, ind) in articles" :key="ind" :article="article" :open-new-tab="true" />
        </div>
        <div v-else class="news__list">
          <ArticleCard v-for="index in 2" :key="index" :loading="true" />
        </div>

        <div class="section-button">
          <nuxt-link :to="localePath('/articles')" class="section-button__button">
            {{ $t('news.read_more') }}
          </nuxt-link>
        </div>
      </div>
    </section>

    <section v-if="!projectsLoading && related.length > 0" class="main__also-watch also-watch">
      <div class="also-watch__container">
        <h2 class="also-watch__title">
          {{ $t('project.also_watch') }}
        </h2>

        <div class="also-watch-box">
          <div v-if="!projectsLoading" class="also-watch__list">
            <div v-for="(proj, ind) in related" :key="ind" class="also-watch-card">
              <div class="also-watch-card__head">
                <nuxt-link :to="$getProjectLink(proj.type, proj.slug)" class="also-watch-card__icon">
                  <nuxt-img
                    :src="$getImage(proj.project_image, 'projects')"
                    :alt="proj.title + $t('common.at') + ' ' + $config.appName"
                    :title="proj.title"
                    :quality="80"
                    format="webp"
                    loading="lazy"
                    @error="$event.target.src = $config.mediaUrl + 'img/projects/default.webp'"
                  />
                </nuxt-link>
                <div class="also-watch-card__stat">
                  <span class="also-watch-card__currency">
                    <i v-if="proj.premium !== null" class="bx bxs-zap premium_icon"></i>
                    {{ proj.title }}
                  </span>
                  <span class="also-watch-card__price">
                    ${{ $priceFormat(proj.price, 2) }}
                  </span>
                  <div :class="{'also-watch-card__rate': true, 'plus': proj.daily_percent > 0, 'minus': proj.daily_percent < 0}">
                    <div class="watchlist-product-rating-day__icon">
                      <i v-if="proj.daily_percent > 0" class="bx bxs-up-arrow"></i>
                      <i v-else-if="proj.daily_percent < 0" class="bx bxs-down-arrow"></i>
                    </div>
                    <div class="watchlist-product-rating-day__percent">
                      {{ proj.daily_percent !== null ? $priceFormat(proj.daily_percent, 2) + '%' : '--' }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="also-watch__list">
            <div v-for="index in 6" :key="index" class="also-watch-card">
              <div class="also-watch-card__head">
                <div class="also-watch-card__icon">
                  <Skeleton width="35px" height="35px" circle />
                </div>
                <div class="also-watch-card__stat">
                  <Skeleton width="60px" height="18px" class="also-watch-card__currency" />
                  <Skeleton width="80px" height="24px" class="also-watch-card__price" />
                  <div class="also-watch-card__rate">
                    <Skeleton width="40px" height="15px" class="watchlist-product-rating-day__percent" />
                  </div>
                </div>
              </div>
              <div class="also-watch-card__graph">
                <Skeleton width="140px" height="50px" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="tooltips">
      <div id="volume_tooltip">
        <p>
          {{ $t('main.volume_tooltip_p1') }}
        </p>
        <nuxt-link :to="localePath('/glossary/trade-volume')" target="_blank" rel="noopener">
          {{ $t('main.read_more') }}
        </nuxt-link>
      </div>
      <div id="volume_base">
        <p>
          {{ $t('project.volume_base_desc') }}
        </p>
      </div>
      <div id="fee_type">
        <p>
          {{ $t('project.fee_type_desc') }}
        </p>
      </div>
      <div id="currency_type">
        <p>
          {{ $t('project.currency_type_desc') }}
        </p>
        <ul>
          <li>
            {{ $t('project.currency_type_cc') }}
          </li>
          <li>
            {{ $t('project.currency_type_cf') }}
          </li>
          <li>
            {{ $t('project.currency_type_fc') }}
          </li>
          <li>
            {{ $t('project.currency_type_ff') }}
          </li>
        </ul>
      </div>
    </div>
  </main>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import {
  hydrateWhenIdle
} from 'vue-lazy-hydration'

import { Skeleton } from 'vue-loading-skeleton'

import BreadCrumbs from '~/components/common/BreadCrumbs'
import Empty from '~/components/misc/Empty'

export default {
  name: 'DefiItemComponent',
  components: {
    BreadCrumbs,
    Empty,
    LineChart: hydrateWhenIdle(() => import('~/components/charts/LineChart')),
    Skeleton
  },
  data() {
    const that = this
    const project = JSON.parse(JSON.stringify(this.$store.getters['projects/project']))
    return {
      loaded: false,
      pages: [
        {
          label: this.$t('common.home'),
          link: '/'
        },
        {
          label: this.$t('common.defi'),
          link: '/defi'
        }
      ],
      dropdowns: {
        pair: false
      },
      categories: ['all', 'spot', 'derivatives', 'perpetual', 'futures'],
      filterPairs: ['all', 'USD', 'EUR', 'USDT', 'USDC'],
      chatFilter: {
        page: 1,
        step: 15,
        pid: parseInt(project.id, 10),
        total: 0
      },
      userMessage: {
        type: 'none',
        message: '',
        reply_to: null,
        pid: parseInt(project.id, 10)
      },
      pairsInit: false,
      pairsFilter: {
        page: 1,
        step: 15,
        category: null,
        fee: null,
        pair: null,
        orderField: 'volume_desc',
        pid: parseInt(project.coinmarketcap_id, 10)
      },
      messagesLoading: false,
      loadingMessage: false,
      chatActionsLoading: {
        like: null,
        report: null,
        delete: null
      },
      copyText: this.$t('common.copy'),
      loadingChart: true,
      lineChart: {
        data: {
          datasets: [
            {
              borderWidth: 2,
              radius: 0,
              fill: true,
              data: [],
              borderColor: 'rgb(0, 183, 74)',
              tension: 0.25,
              pointRadius: 7,
              pointBorderColor: 'rgba(0, 0, 0, 0)',
              pointBackgroundColor: 'rgba(0, 0, 0, 0)',
              pointHoverBackgroundColor: 'rgba(0, 183, 74, .75)',
              pointHoverBorderColor: 'rgba(255, 255, 255, 1)'
            }
          ]
        },
        options: {
          responsive: true,
          parsing: false,
          normalized: true,
          spanGaps: true,
          maintainAspectRatio: false,
          interaction: {
            intersect: false,
            mode: 'index'
          },
          scales: {
            xAxes: [{
              ticks: {
                autoSkip: true,
                maxRotation: 0,
                minRotation: 0
              },
              gridLines: {
                color: 'rgba(45, 45, 45, 0)'
              }
            }],
            yAxes: [{
              ticks: {
                autoSkip: true,
                maxRotation: 0,
                minRotation: 0,
                callback(value, index, ticks) {
                  const volumeValue = parseFloat(value)
                  return '$' + (volumeValue > 1000 ? that.$getBigNumber(value) : that.$priceFormat(volumeValue, 2))
                }
              },
              gridLines: {
                color: 'rgba(45, 45, 45, 0)'
              }
            }]
          },
          datasets: {
            line: {
              pointRadius: 0
            }
          },
          elements: {
            point: {
              radius: 0
            }
          },
          plugins: {
            legend: false
          },
          tooltips: {
            enabled: true,
            footerFontStyle: 'normal',
            callbacks: {
              title: (context) => {
                const date = context[0].xLabel.substring(0, context[0].xLabel.length - 3)
                const hours = context[0].xLabel.substring(context[0].xLabel.length - 2, context[0].xLabel.length)
                return that.$getFormatDate(date + ' ' + hours + ':00:00', true)
              },
              label: (tooltipItems) => {
                const volume = parseFloat(tooltipItems.yLabel)
                return '$' + (volume > 1000000 ? that.$getBigNumber(volume, 1) : that.$priceFormat(volume, 2))
              },
              labelColor: (context, data) => {
                const prevValue = data.config.data.datasets[context.datasetIndex].data[context.index - 1]
                const currentValue = data.config.data.datasets[context.datasetIndex].data[context.index]
                const color = prevValue !== undefined && currentValue < prevValue ? 'rgb(219, 68, 55)' : 'rgb(0, 183, 74)'
                return {
                  borderColor: 'rgba(255, 255, 255, 0)',
                  backgroundColor: color,
                  borderWidth: 0,
                  borderRadius: 8
                }
              }
            }
          }
        }
      }
    }
  },
  async fetch({ store, error, route }) {
    try {
      await store.dispatch('projects/fetchProject', {
        slug: route.params.slug
      })
    } catch (err) {
      error(err)
    }
  },
  head({ $config: { appUrl } }) {
    return {
      title: this.project ? this.project.title : this.$t('common.launching'),
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.project ? this.project.description : ''
        },
        {
          hid: 'og-description',
          name: 'og:description',
          content: this.project ? this.project.description : ''
        },
        {
          hid: 'og-title',
          name: 'og:title',
          content: this.project
            ? this.project.title
            : this.$t('common.launching')
        },
        {
          hid: 'og-image',
          name: 'og:image',
          content: this.project
            ? this.project.image
            : appUrl + 'seo/default.jpg'
        }
      ]
    }
  },
  computed: {
    ...mapGetters(['tippyOptions']),
    ...mapGetters('projects', [
      'project',
      'related',
      'pairs',
      'pairsTotal',
      'pairsLoading',
      'watchlistLoading'
    ]),
    ...mapGetters('chat', [
      'messages',
      'total',
      'loading'
    ]),
    ...mapGetters('auth', [
      'user',
      'watchlist'
    ]),
    ...mapGetters('news', [
      'articles'
    ]),
    projectsLoading() {
      return this.$store.getters['projects/loading']
    },
    articlesLoading() {
      return this.$store.getters['news/loading']
    },
    insideWatchlist() {
      return this.watchlist.includes(this.project.id)
    },
    replyTo() {
      return this.userMessage.reply_to !== null ? this.messages.find(itm => itm.mid === this.userMessage.reply_to) : null
    }
  },
  created() {
    this.$nuxt.$on('changeCurrency', this.changeCurrency)
  },
  mounted() {
    this.pages.push({
      label: this.project.title,
      link: ''
    })
    window.addEventListener('click', this.handleClickEvents)
    this.loadChart()
    this.fetchMessages(this.chatFilter).then(() => {
      if (this.$refs.chatArea !== undefined) {
        this.$refs.chatArea.addEventListener('scroll', this.handleChatScroll)
      }
    })
    this.fetchProjectArticles({
      page: 1,
      step: 2,
      pid: this.project.id
    })
    this.fetchRelated({
      ...this.filter,
      type: 'defi',
      except: this.project.id
    })
      .then(() => {
        this.projectsLoaded = true
      })
    this.fetchPairs(this.pairsFilter).then(() => {
      this.pairsInit = true
    })
  },
  beforeDestroy() {
    window.removeEventListener('click', this.handleClickEvents)
    if (this.$refs.chatArea !== undefined) {
      this.$refs.chatArea.removeEventListener('scroll', this.handleChatScroll)
    }
  },
  methods: {
    ...mapActions('chat', [
      'fetchMessages',
      'sendMessage',
      'removeMessage',
      'removeMessageLike',
      'addMessageLike'
    ]),
    ...mapActions('auth', [
      'fetchWatchlist',
      'addToWatchlist',
      'removeFromWatchlist'
    ]),
    ...mapActions('news', [
      'fetchProjectArticles'
    ]),
    ...mapActions('projects', [
      'fetchRelated',
      'fetchPairs'
    ]),
    toggleDropdown(dropdownIndex) {
      if (this.dropdowns[dropdownIndex] !== undefined && this.dropdowns[dropdownIndex]) {
        this.closeDropdowns()
      } else {
        this.$set(this.dropdowns, dropdownIndex, true)
      }
    },
    closeDropdowns() {
      for (const prop in this.dropdowns) {
        if (Object.prototype.hasOwnProperty.call(this.dropdowns, prop)) {
          this.$set(this.dropdowns, prop, false)
        }
      }
    },
    toggleWatchlist() {
      if (this.insideWatchlist) {
        this.removeFromWatchlist({
          id: this.project.id
        }).then(() => {
          this.project.watchlists -= 1
          this.$toast.fire({
            icon: 'success',
            title: this.$t('watchlist.updated')
          })
        })
      } else {
        this.addToWatchlist({
          id: this.project.id
        }).then(() => {
          this.project.watchlists += 1
          this.$toast.fire({
            icon: 'success',
            title: this.$t('watchlist.updated')
          })
        })
      }
    },
    setCommentType(newType = 'none') {
      if (newType !== undefined && this.userMessage.type !== newType.toLowerCase()) {
        this.userMessage.type = newType.toLowerCase()
      }
    },
    getContentById(elem) {
      if (typeof document !== 'undefined') {
        return document.getElementById(elem) !== null ? document.getElementById(elem).innerHTML : '...'
      } else {
        return '...'
      }
    },
    async handleChatScroll() {
      if (this.messages !== undefined && this.$refs.chatArea !== undefined && (this.$refs.chatArea.scrollTop + 100) >= this.$refs.chatArea.offsetHeight &&
        this.messages.length < this.total && !this.messagesLoading) {
        this.chatFilter.page += 1
        this.messagesLoading = true
        await this.fetchMessages(this.chatFilter).then(() => {
          this.messagesLoading = false
        }).catch((err) => {
          console.log(err)
          this.messagesLoading = false
        })
      }
    },
    sendChatMessage() {
      if (this.user !== null) {
        this.loadingMessage = true
        this.sendMessage(this.userMessage).then(() => {
          this.loadingMessage = false
          this.clearMessageForm()
          this.$toast.fire({
            icon: 'success',
            title: this.$t('project.message_sent')
          })
        }).catch((err) => {
          console.log(err)
        })
      }
    },
    likeMessage(mid) {
      if (this.user !== null) {
        this.chatActionsLoading.like = mid
        this.addMessageLike(mid).then(() => {
          this.chatActionsLoading.like = null
        }).catch((err) => {
          console.log(err)
        })
      }
    },
    unlikeMessage(mid) {
      if (this.user !== null) {
        this.chatActionsLoading.like = mid
        this.removeMessageLike(mid).then(() => {
          this.chatActionsLoading.like = null
        }).catch((err) => {
          console.log(err)
        })
      }
    },
    removeMessage(mid) {
      const deleteableMessage = this.messages.find(itm => itm.mid === mid)
      if (this.user !== null && deleteableMessage !== undefined && deleteableMessage.uid === this.user.id) {
        this.chatActionsLoading.delete = mid
        this.removeMessage(mid).then(() => {
          this.chatActionsLoading.delete = null
          this.$toast.fire({
            icon: 'success',
            title: this.$t('project.message_deleted')
          })
        }).catch((err) => {
          console.log(err)
        })
      }
    },
    setReplyTo(mid) {
      if (this.userMessage.reply_to === parseInt(mid, 10)) {
        this.userMessage.reply_to = null
      } else {
        this.userMessage.reply_to = parseInt(mid, 10)
      }
    },
    clearMessageForm() {
      this.userMessage.type = 'none'
      this.userMessage.message = ''
      this.userMessage.reply_to = null
    },
    copy(evt, copyMessage) {
      evt.stopPropagation()
      navigator.clipboard.writeText(copyMessage)
      this.copyText = this.$t('common.copied')
      setTimeout(() => {
        this.copyText = this.$t('common.copy')
      }, 1200)
    },
    changeFilterPair(filterPair) {
      this.pairsFilter.pair = filterPair !== 'all' && filterPair !== this.pairsFilter.pair ? filterPair : null
      this.closeDropdowns()
      this.reloadPairs()
    },
    reloadPairs() {
      if (!this.pairsLoading) {
        this.pairsFilter.page = 1
        this.fetchPairs(this.pairsFilter)
      }
    },
    loadMorePairs() {
      if (!this.pairsLoading) {
        this.pairsFilter.page += 1
        this.fetchPairs(this.pairsFilter)
      }
    },
    loadChart() {
      if (this.project.price_change === null) {
        return
      }
      try {
        const data = JSON.parse(this.project.price_change)
        const values = []
        const labels = []
        for (const [key, value] of Object.entries(data)) {
          labels.push(key)
          values.push(parseFloat(value))
        }
        let chartColor = '0, 183, 74'
        if (values[values.length - 2] !== undefined && values[values.length - 2] > values[values.length - 1]) {
          chartColor = '219, 68, 55'
        }
        const goesUp = (ctx, value) => ctx.p0.parsed.y < ctx.p1.parsed.y ? value : undefined
        const goesDown = (ctx, value) => ctx.p0.parsed.y > ctx.p1.parsed.y ? value : undefined
        this.lineChart.data.labels = labels
        this.lineChart.data.datasets[0].data = values
        this.lineChart.data.datasets[0].pointHoverBackgroundColor = `rgb(${chartColor})`
        this.lineChart.data.datasets[0].borderColor = `rgb(${chartColor})`
        this.lineChart.data.datasets[0].backgroundColor = (context) => {
          const chart = context.chart
          const { ctx, chartArea } = chart

          if (!chartArea) {
            return
          }
          const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top)
          gradient.addColorStop(0, 'rgba(' + chartColor + ', 0)')
          gradient.addColorStop(0.5, 'rgba(' + chartColor + ', .35)')
          gradient.addColorStop(1, 'rgba(' + chartColor + ', 1)')

          return gradient
        }
        this.lineChart.data.datasets[0].segment = {
          borderColor: ctx => goesUp(ctx, 'rgb(0, 183, 74)') || goesDown(ctx, 'rgb(219, 68, 55)')
        }
        const that = this
        this.lineChart.options.scales.xAxes[0].ticks = {
          display: true,
          callback(value, index, ticks) {
            const numberOfLabels = 3
            if (index % numberOfLabels === 0) {
              return that.$getDateDayAndMonth(labels[index].substring(0, labels[index].length - 3), false)
            } else {
              return ''
            }
          }
        }
        this.$nuxt.$emit('lineChartUpdate')
        this.loadingChart = false
      } catch (err) {
        this.loadingChart = false
        console.error(err)
      }
    },
    getPairShortcut(pair) {
      if (pair === undefined || pair === null) {
        return '...'
      }
      const pairTypes = pair.split('-')
      return pairTypes[0] !== undefined && pairTypes[1] !== undefined ? pairTypes[0].charAt(0).toUpperCase() + '-' + pairTypes[1].charAt(0).toUpperCase() : '...'
    },
    handleClickEvents(evt) {
      if (
        !evt.target.closest('.select') &&
          !evt.target.closest('.select__body') &&
          !evt.target.closest('.select__current') &&
          !evt.target.closest('.select__header')
      ) {
        this.closeDropdowns()
      }
    }
  }
}
</script>

<style scoped>
  @import url('~/assets/css/exchange.css');
  @import url('~/assets/css/coin-page.css');
</style>
